import { useCityStore } from '@store/city';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useCategoryDetail } from '@api/catalog/categories';
import { getPagination } from '@api/common/helpers';
import { useSeoTags } from '@api/seo';

import { BlockBreadcrumbs } from '@components/BlockBreadcrumbs';
import CategoryPanel from '@components/CategoryPanel';
import { ExpandableBlock } from '@components/ExpandableBlock';
import PageWrapper from '@components/PageWrapper';
import ProductCarousel from '@components/ProductCarousel';

import { Layout, Section, colors, scale, typography } from '@scripts/gds';
import { declOfNum, getIdBySeoType, getSeoFilter } from '@scripts/helpers';
import { useMedia } from '@scripts/hooks';
import { useGetProductsList } from '@scripts/hooks/useGetProductsList';
import { useGetUserData } from '@scripts/hooks/useGetUserData';

import { getCorrectPathname } from '@utils/getCorrectPathname';
import { getCategoryProducts } from '../helper';
import BrandsCarousel from './BrandsCarousel';
import VirtualCategories from './VirtualCategories';

const CAROUSEL_ITEMS = 12;
const searchParams = {
    sort: 'id',
    pagination: getPagination(CAROUSEL_ITEMS),
    include: ['brand', 'category', 'images', 'attributes', 'seller'],
};

const HighLevelCategory = () => {
    const { asPath, query } = useRouter();
    const pathname = getCorrectPathname(asPath);
    const currentCity = useCityStore(({ currentCity }) => currentCity);

    const seoItemId = getIdBySeoType(pathname, query);
    const { data: seoQueryData } = useSeoTags(getSeoFilter(pathname, seoItemId), Boolean(seoItemId));
    const { data: { title = '', description = '', h1 = '', seo_text = '' } = {} } = seoQueryData || {};

    const categoryCode = String(query.category);
    const { xs, sm } = useMedia();

    const { data: apiDataCategory, error: errorCategory } = useCategoryDetail(
        {
            code: categoryCode,
        },
        Boolean(query.category)
    );

    const category = useMemo(() => apiDataCategory?.data, [apiDataCategory]);
    const breadcrumbsCategory = useMemo(
        () => [
            {
                name: 'Главная',
                link: '/',
            },
            {
                name: category?.name || '',
                link: `/catalog/${categoryCode}`,
            },
        ],
        [category, categoryCode]
    );

    const { user, apiUser } = useGetUserData();

    const { productsList } = useGetProductsList(
        {
            ...searchParams,
            customer_region_guid: currentCity.city_guid,
            pagination: getPagination(CAROUSEL_ITEMS * (category?.children.length || 0)),
            filter: { category_id: category?.children.map(c => c.id) },
        },
        {
            enabled: !user && Boolean(category),
            enabledAuth: Boolean(apiUser && category),
        }
    );

    return !errorCategory && category ? (
        <PageWrapper
            seoProps={{
                title: title || `${category.name} купить в интернет-магазине «SecuMarket»`,
                description:
                    description ||
                    `${category?.name} различных производителей и брендов купить по выгодным ценам с доставкой в любой регион России в интернет-магазине «SecuMarket».`,
            }}
        >
            <Section>
                <Layout cols={{ xxxl: 12, sm: 6 }} gap={{ xxxl: scale(8), xs: scale(5) }}>
                    <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                        <Layout cols={{ xxxl: 12, sm: 6 }} gap={scale(3)}>
                            <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                <BlockBreadcrumbs items={breadcrumbsCategory} />
                            </Layout.Item>

                            <Layout.Item
                                css={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    [sm]: { flexDirection: 'column' },
                                }}
                                col={{ xxxl: 12, sm: 6 }}
                            >
                                <h1
                                    css={{
                                        ...typography('h2'),
                                        margin: '0.11em 0',
                                        marginRight: scale(2),
                                        [sm]: { margin: '4px' },
                                        [xs]: { margin: 0 },
                                    }}
                                >
                                    {h1 || category.name}
                                </h1>

                                <span css={{ color: colors.textNuanceLight }}>
                                    {category.offers_count}{' '}
                                    {declOfNum(category.offers_count, ['товар', 'товара', 'товаров'])}
                                </span>
                            </Layout.Item>

                            {category.children.map(panel => (
                                <Layout.Item
                                    key={panel.id}
                                    col={{ xxxl: 2, md: 3, sm: 2, xs: 2, xxs: 3 }}
                                    css={{ height: scale(23) }}
                                >
                                    <CategoryPanel
                                        link={`/catalog/${panel.code}`}
                                        image={panel.image}
                                        name={panel.name}
                                    />
                                </Layout.Item>
                            ))}
                        </Layout>
                    </Layout.Item>

                    <Layout.Item col={{ xxxl: 12, sm: 6 }} css={{ width: '100%' }}>
                        <BrandsCarousel />
                    </Layout.Item>

                    {apiDataCategory?.data?.virtual_categories?.length ? (
                        <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                            <VirtualCategories virtual_categories={apiDataCategory.data.virtual_categories} />
                        </Layout.Item>
                    ) : null}

                    {category.children?.map(c =>
                        getCategoryProducts(productsList, c.id)?.length ? (
                            <Layout.Item col={{ xxxl: 12, sm: 6 }} css={{ width: '100%' }} key={c.id}>
                                <ProductCarousel
                                    products={getCategoryProducts(productsList, c.id)}
                                    title={c.name}
                                    link={`/catalog/${c.code}`}
                                />
                            </Layout.Item>
                        ) : null
                    )}

                    {seo_text && (
                        <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                            <ExpandableBlock height={scale(15)}>
                                <span css={{ marginTop: scale(6) }} dangerouslySetInnerHTML={{ __html: seo_text }} />
                            </ExpandableBlock>
                        </Layout.Item>
                    )}
                </Layout>
            </Section>
        </PageWrapper>
    ) : null;
};

export default HighLevelCategory;
